<template>
  <div class="view" v-if="canView" v-cloak>
    <GridPanelHeader
      :title="$t('tools.label_management.title')"
      :grid-ref="$refs[gridRefKey]"
      :show-add="canEdit"
      :show-filter="false"
      @add="handleAddClick"
    />
    <DxDataGrid
      key-expr="uuid"
      :ref="gridRefKey"
      :data-source="labelsDataSource"
      :remote-operations="gridRemoteOperations"
      :column-min-width="80"
      style="min-height: 350px"
      :show-column-lines="true"
      :show-row-lines="false"
      :show-borders="true"
      :showBorders="true"
      :column-auto-width="false"
      :allowColumnReordering="true"
      :allowColumnResizing="true"
      :row-alternation-enabled="true"
      :element-attr="gridAttributes"
      @editing-start="onEditingStart"
      :row-height="50"
    >
      <DxColumn
        dataField="name"
        :caption="$t('tools.label_management.name')"
        :allowHeaderFiltering="false"
        :filter-operations="['contains']"
        :allowSorting="false"
      >
        <DxRequiredRule :message="$t('validation.field_is_required')" />
      </DxColumn>
      <DxColumn
        dataField="type_code"
        :caption="$t('tools.label_management.type_code')"
        :allowHeaderFiltering="false"
        :filter-operations="['contains']"
        :allowSorting="false"
        :lookup="{ dataSource: labelTypeCodes, valueExpr: 'value', displayExpr: 'name' }"
      >
        <DxRequiredRule :message="$t('validation.field_is_required')" />
      </DxColumn>
      <DxColumn
        :width="70"
        alignment="center"
        :fixed="true"
        fixed-position="right"
        :allow-sorting="false"
        :allow-filtering="false"
        :show-in-column-chooser="false"
        cell-template="actionsCell"
        :editorOptions="{ disabled: true }"
      />
      <template #actionsCell="{ data }">
        <DxActionMenu
          :grid-ref="$refs[gridRefKey]"
          row-key="uuid"
          :row="data.data"
          :can-edit="canEdit"
          :can-delete="canDelete"
          :allow-deleting="() => true"
        />
      </template>
      <DxEditing mode="form" :allowUpdating="false" :allowAdding="false" :allowDeleting="false" :confirm-delete="true" :use-icons="true">
        <DxTexts
          :confirm-delete-message="$t('common.confirm_delete', { title: $t('tools.label_management.title') })"
          :confirm-delete-title="$t('common.confirm_delete_title')"
          :save-row-changes="$t('common.save')"
          :cancel-row-changes="$t('common.close')"
          :addRow="$t('common.add_new')"
          :editRow="$t('common.edit')"
          :deleteRow="$t('common.delete')"
        />
      </DxEditing>
      <DxPaging :total="3500" :page-size="15" />
      <DxPager
        :visible="true"
        :allowed-page-sizes="pageSizes"
        display-mode="compact"
        :show-page-size-selector="true"
        :show-info="true"
        :show-navigation-buttons="true"
        :info-text="$t('common.page_info')"
      />
      <DxHeaderFilter :visible="true" />
      <DxFilterRow :visible="true" :showOperationChooser="true" apply-filter="auto" />
      <DxSelection mode="single" />
      <DxSearchPanel :visible="false" />
      <DxGroupPanel :visible="false" />
      <DxExport :enabled="false" :allow-export-selected-data="false" :fileName="'Labels'" />
      <DxColumnChooser :enabled="false" mode="select" />
      <DxColumnFixing :enabled="true" />
    </DxDataGrid>
  </div>
  <div v-else>
    <forbidden></forbidden>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import DxDataGrid, {
  DxColumn,
  DxPaging,
  DxPager,
  DxEditing,
  DxHeaderFilter,
  DxFilterRow,
  DxSelection,
  DxTexts,
  DxSearchPanel,
  DxExport,
  DxGroupPanel,
  DxColumnChooser,
  DxColumnFixing,
  DxRequiredRule,
} from "devextreme-vue/data-grid";
import Forbidden from "~/pages/errors/Forbidden.vue";
import CustomStore from "devextreme/data/custom_store";
import { canView, canDelete, canEdit, permissions, permissionTypes } from "~/helpers/Permissions";
import notify from "devextreme/ui/notify";
import GridPanelHeader from "@/components/DxGrid/GridPanelHeader.vue";
import DxActionMenu from "@/components/DxGrid/DxActionMenu.vue";

const LABEL_TYPE_CODES = ["PRODUCT_LOT_CATEGORY"];

export default {
  name: "LabelManagement",
  components: {
    DxDataGrid,
    DxColumn,
    DxEditing,
    DxHeaderFilter,
    DxFilterRow,
    DxSelection,
    DxTexts,
    DxPaging,
    DxPager,
    DxSearchPanel,
    DxExport,
    DxGroupPanel,
    DxColumnChooser,
    DxColumnFixing,
    DxRequiredRule,
    Forbidden,
    GridPanelHeader,
    DxActionMenu,
  },
  data() {
    return {
      gridAttributes: {
        class: "smagria-grid label-management-grid",
      },
      gridRefKey: "labelManagementGrid",
      labelsDataSource: new CustomStore({
        key: "uuid",
        load: async (loadOptions) => {
          let dataSource = { data: [], totalCount: 0 };
          let pageLimit = loadOptions.isLoadingAll ? 99999 : loadOptions.take;
          let pageNumber = (loadOptions.skip + loadOptions.take) / pageLimit;
          let payload = {
            page: pageNumber,
            limit: pageLimit,
            filter: loadOptions.filter,
            sort: loadOptions.sort,
          };
          try {
            await this.getLabels(payload).then((response) => {
              dataSource.data = response.data.data;
              dataSource.totalCount = response.data?.pagination?.total ?? 1;
            });
            return dataSource;
          } catch (error) {
            console.error("Error fetching labels:", error);
            throw error;
          }
        },
        insert: this.insertLabel,
        update: this.updateLabelRow,
        remove: this.removeLabel,
      }),
      gridRemoteOperations: {
        paging: true,
        filtering: true,
        sorting: true,
      },
      pageSizes: [15, 25, 100],
    };
  },
  computed: {
    config() {
      return {
        permission: permissions.LABEL_MANAGEMENT,
        permission_type: permissionTypes.access,
      };
    },
    canView() {
      return canView(this);
    },
    canEdit() {
      return canEdit(this);
    },
    canDelete() {
      return canDelete(this);
    },
    labelTypeCodes() {
      return LABEL_TYPE_CODES.map((code) => ({
        value: code,
        name: this.$t(`tools.label_management.type_codes.${code}`),
      }));
    },
    dataGridInstance() {
      const grid = this.$refs && this.$refs[this.gridRefKey] ? this.$refs[this.gridRefKey] : null;
      return grid && grid.instance ? grid.instance : null;
    },
  },
  methods: {
    ...mapActions({
      getLabels: "labelManagement/getLabels",
      createLabel: "labelManagement/createLabel",
      updateLabel: "labelManagement/updateLabel",
      deleteLabel: "labelManagement/deleteLabel",
    }),
    handleAddClick() {
      const instance = this.dataGridInstance;
      if (instance) {
        instance.addRow();
      }
    },
    onEditingStart(e) {
      if (!this.canEdit) {
        e.cancel = true;
      }
    },
    async insertLabel(obj) {
      try {
        const response = await this.createLabel(obj);
        this.notifyMsg(response);
      } catch (error) {
        console.error("Error creating label:", error);
        throw error;
      }
    },
    async updateLabelRow(key, obj) {
      try {
        const response = await this.updateLabel({
          ...obj,
          uuid: key,
        });
        this.notifyMsg(response);
      } catch (error) {
        console.error("Error updating label:", error);
        throw error;
      }
    },
    async removeLabel(key) {
      try {
        const response = await this.deleteLabel({ uuid: key });
        this.notifyMsg(response);
      } catch (error) {
        console.error("Error deleting label:", error);
        throw error;
      }
    },
    notifyMsg(response) {
      notify({
        message: response.message ?? "",
        type: response.status === "ok" ? "success" : "error",
        displayTime: 2500,
        width: "auto",
        position: { at: "top center", my: "top center" },
      });
    },
  },
};
</script>
